import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  AbstractControl,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import moment from 'moment';
import { HttpErrorResponse } from '@angular/common/http';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';

import { SchedulesService } from '../../core/services/http-services/schedules.service';
import { CustomValidationService } from './../../core/services/utils/custom-validation.service';

import { Schedule } from '../../core/models/entities/schedule.type';
import { ScheduleModalMode } from '../../core/enums/schedule-model-mode.enum';
import { ScheduleSettingsMode } from '../../core/enums/screen-settings-mode.enum';
import { CreateScheduleDTO } from '../../core/models/view-models/schedule-models/create-schedule-dto.model';
import { UpdateScheduleDTO } from '../../core/models/view-models/schedule-models/update-schedule-dto.model';
import { ScheduleDialogMessagesEnum } from './../../core/enums/toastr-messages/schedule-dialog-messages.enum';

@Component({
  selector: 'schedule-settings',
  templateUrl: './schedule-settings.component.html',
  styleUrls: ['./schedule-settings.component.scss'],
})
export class ScheduleSettingsComponent implements OnInit, OnDestroy {
  @Input() public scheduleModelMode: ScheduleModalMode;
  @Input() public schedule: Schedule = new Schedule();

  public scheduleSettingsMode: ScheduleSettingsMode = ScheduleSettingsMode.Daily;
  public scheduleSettingsModes = ScheduleSettingsMode;
  public scheduleModalModes = ScheduleModalMode;
  public startTime: Date;
  public endTime: Date;
  public weekdays: string[] = [];
  public longTermMode = false;
  public form: UntypedFormGroup;
  public minDate = new Date(new Date().setHours(0, 0, 0));

  private subscriptions: Subscription = new Subscription();
  private saveIsInProgress: boolean = false;
  private readonly momentTimeFormat = 'HH:mm:ss';
  private readonly momentDateFormat = 'YYYY-MM-DD';

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly toastr: ToastrService,
    private readonly scheduleService: SchedulesService,
    private readonly customValidators: CustomValidationService,
    private readonly dialogRef: MatDialogRef<ScheduleSettingsComponent>
  ) { }

  ngOnInit(): void {
    if (this.schedule) {
      this.scheduleSettingsMode =
        this.scheduleModelMode === ScheduleModalMode.TurnOn
          ? ScheduleSettingsMode.On
          : this.schedule.ScheduleType;
    }

    this.initForm();
    this.patchForm();
    this.updateFormFields();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onScheduleSettingChange(event: Event, option: ScheduleSettingsMode): void {
    const target = event.target as HTMLInputElement;
    if (target.checked) {
      this.scheduleSettingsMode = option;
      this.form.get('ScheduleSettingsMode').setValue(this.scheduleSettingsMode);
    }

    this.longTermMode = false;
    this.updateFormFields();
  }

  save(): void {
    this.schedule.ScheduleType = this.scheduleSettingsMode.valueOf();

    if (this.scheduleSettingsMode !== ScheduleSettingsMode.Weekly) {
      Object.keys((this.WeekDays as UntypedFormGroup).controls).forEach((control) =>
        this.WeekDays.get(control).patchValue(false)
      );
    }

    if (this.form.valid && !this.saveIsInProgress) {
      this.saveIsInProgress = true;
      if (this.scheduleModelMode === ScheduleModalMode.Create) {
        this.createSchedule();
      } else if (this.scheduleModelMode === ScheduleModalMode.Edit) {
        this.updateSchedule();
      }
    } else if (!this.form.valid) {
      this.toastr.error('Fix the form errors before saving changes', 'Some error occured');
    }
  }

  turnOff(): void {
    if (this.schedule && this.schedule.Id) {
      this.subscriptions.add(
        this.scheduleService.turnOffSchedule(this.schedule.Id).subscribe(() => {
          this.dialogRef.close();
          this.toastr.success(
            ScheduleDialogMessagesEnum.TurnOffScheduleSuccess,
            ScheduleDialogMessagesEnum.TurnOffScheduleTitle
          );
        })
      );
    } else {
      this.toastr.error(ScheduleDialogMessagesEnum.TurnOffScheduleError);
    }
  }

  delete(): void {
    if (this.schedule && this.schedule.Id) {
      this.subscriptions.add(
        this.scheduleService.deleteSchedule(this.schedule.Id).subscribe(() => {
          this.dialogRef.close();
          this.toastr.success(
            ScheduleDialogMessagesEnum.DeleteScheduleSuccess,
            ScheduleDialogMessagesEnum.DeleteScheduleTitle
          );
        })
      );
    } else {
      this.toastr.error(ScheduleDialogMessagesEnum.DeleteScheduleError);
    }
  }

  turnOn(): void {
    this.subscriptions.add(
      this.scheduleService.turnOnSchedule(this.schedule.Id).subscribe(() => {
        this.dialogRef.close();
        this.toastr.success(
          ScheduleDialogMessagesEnum.TurnOnScheduleSuccess,
          ScheduleDialogMessagesEnum.TurnOnScheduleTitle
        );
      })
    );
  }

  onAllDayValueChanged(isChecked: boolean): void {
    if (isChecked) {
      this.form.get('StartTime').reset();
      this.form.get('EndTime').reset();
      if (this.scheduleSettingsMode !== this.scheduleSettingsModes.Range) {
        this.form.get('StartDate').reset();
        this.form.get('EndDate').reset();
      }
    } else {
      this.initFormStartAndEndDates();
    }
  }

  private initForm(): void {
    this.weekdays = moment.weekdays(true);
    this.weekdays.push(this.weekdays.shift());

    this.form = this.fb.group({
      AllDay: [false],
      StartTime: [new Date(), [Validators.required]],
      EndTime: [new Date(), [Validators.required]],
      StartDate: [new Date(), [Validators.required]],
      EndDate: [new Date(), [Validators.required]],
      ScheduleSettingsMode: [this.scheduleSettingsMode]
    });

    this.initFormStartAndEndDates();

    const weekdaysControl = new UntypedFormGroup({});
    this.weekdays.forEach((day) => weekdaysControl.addControl(day, new UntypedFormControl(false)));
    this.form.addControl('WeekDays', weekdaysControl);

    this.form.setValidators([
      this.customValidators.timeValidator('StartTime', 'EndTime', 'StartDate', 'EndDate', 'AllDay', 'ScheduleSettingsMode', 'WeekDays'),
    ]);
  }

  private initFormStartAndEndDates(): void {
    const startTime = this.getTimeValue(new Date());
    const increasesDate = new Date(
      new Date().setMinutes(
        new Date().getMinutes() + 1
      )
    );
    const endTime = this.getTimeValue(increasesDate);

    this.form.get('StartTime').setValue(startTime);
    this.form.get('EndTime').setValue(endTime);
    this.form.get('StartDate').setValue(new Date());
    this.form.get('EndDate').setValue(new Date());
  }

  private patchForm(): void {
    Object.entries(this.schedule).forEach(([key, value]) => {
      const control = this.form.get(key) || this.WeekDays.get(key);

      if (!control || !value) {
        return;
      }

      const isDate = typeof value === 'string' && moment(value, [this.momentDateFormat, this.momentTimeFormat]).isValid();
      if (isDate) {
        const date = moment(value, [this.momentTimeFormat, this.momentDateFormat]).toDate();
        if (key === 'StartTime' || key === 'EndTime') {
          const newDate = this.getTimeValue(date);
          control.patchValue(newDate);
        } else {
          control.patchValue(date);
        }
        if (key === 'StartDate') {
          this.minDate = date;
        }
      } else {
        control.patchValue(value);
      }
    });
  }

  private getTimeValue(date: Date): string {
    const hours: string = this.getTwoDigitsNumber(date.getHours());
    const minutes: string = this.getTwoDigitsNumber(date.getMinutes());
    const seconds: string = this.getTwoDigitsNumber(date.getSeconds());
    const time: string = `${hours}:${minutes}:${seconds}`;

    return time;
  }

  private getTwoDigitsNumber(number: number): string {
    return ('0' + number).slice(-2);
  }

  private updateFormFields() {
    if (this.scheduleSettingsMode !== ScheduleSettingsMode.Range) {
      this.EndDate.disable();
      this.StartDate.disable();
    } else {
      this.EndDate.enable();
      this.StartDate.enable();
      if (this.StartDate && !this.StartDate.value) {
        this.form.get('StartDate').setValue(new Date());
        this.form.get('EndDate').setValue(new Date());
      }
    }

    if (this.scheduleSettingsMode !== ScheduleSettingsMode.Weekly) {
      setTimeout(() => this.WeekDays.setErrors(null));
    }
  }

  private createSchedule(): void {
    const { WeekDays = {}, ...data } = this.form.value;
    const scheduleDTO = new CreateScheduleDTO(
      Object.assign({ ...this.schedule }, data, WeekDays)
    );

    this.subscriptions.add(
      this.scheduleService.createSchedule(scheduleDTO).subscribe(() => {
        this.dialogRef.close();
        this.toastr.success(
          ScheduleDialogMessagesEnum.ApplyScheduleSuccess,
          ScheduleDialogMessagesEnum.ApplyScheduleTitle
        );
      }, (erResp: HttpErrorResponse) => {
        this.form.setErrors(erResp.error.message);
        this.saveIsInProgress = false;
      })
    );
  }

  private updateSchedule(): void {
    const { WeekDays = {}, ...data } = this.form.value;
    const model = new UpdateScheduleDTO(
      Object.assign({ ...this.schedule }, data, WeekDays)
    );

    this.subscriptions.add(
      this.scheduleService.updateSchedule(model).subscribe(() => {
        this.dialogRef.close();
        this.toastr.success(
          ScheduleDialogMessagesEnum.UpdateScheduleSuccess,
          ScheduleDialogMessagesEnum.UpdateScheduleTitle
        );
      }, (erResp: HttpErrorResponse) => {
        this.form.setErrors(erResp.error.message);
        this.saveIsInProgress = false;
      })
    );
  }

  // Form getters

  get StartTime(): AbstractControl {
    return this.form.get('StartTime');
  }

  get EndTime(): AbstractControl {
    return this.form.get('EndTime');
  }

  get StartDate(): AbstractControl {
    return this.form.get('StartDate');
  }

  get EndDate(): AbstractControl {
    return this.form.get('EndDate');
  }

  get AllDay(): AbstractControl {
    return this.form.get('AllDay');
  }

  get WeekDays(): AbstractControl {
    return this.form.get('WeekDays');
  }
}
